import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Mail, Phone, Shield, Lock, Eye, FileText, Globe, UserCheck } from 'lucide-react';

export const Privacy = () => {
  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-subtle">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge variant="outline" className="mb-4">Legal</Badge>
            <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-6">
              Data Protection Notice
            </h1>
            <p className="text-lg text-muted-foreground">
              For Customers of SusTek Pte. Ltd.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Revised on: 8 February 2026 (with updated revision to PDPA 2021)
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-slate max-w-none">
            
            {/* Introduction */}
            <Card className="mb-8 border-border/50">
              <CardContent className="pt-6">
                <p className="text-muted-foreground leading-relaxed">
                  This Data Protection Notice ("Notice") sets out the basis which SusTek Pte. Ltd. ("we", "us", or "our") may collect, use, disclose or otherwise process personal data of our customers in accordance with the Personal Data Protection Act ("PDPA"). This Notice applies to personal data in our possession or under our control, including personal data in the possession of organisations which we have engaged to collect, use, disclose or process personal data for our purposes.
                </p>
              </CardContent>
            </Card>

            {/* Organisation Overview */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-teal/10 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-teal" />
                </div>
                <h2 className="text-2xl font-bold text-foreground m-0">Organisation Overview</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-4">
                SusTek engineers trusted sustainability data systems and credibility engines for reporting and sustainability finance. In a world full of bold sustainability claims, trust is the real scarce resource. We design and build the credibility infrastructure that turns sustainability data into finance-grade evidence—so capital can move with confidence toward real environmental impact.
              </p>
              <h3 className="text-lg font-semibold text-foreground mt-6 mb-3">What We Do</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li><strong className="text-foreground">Data Engineering:</strong> We build robust ESG data foundations—pipelines, data lakes, and quality controls—that ensure sustainability data is accurate, traceable, and decision-ready.</li>
                <li><strong className="text-foreground">Reporting & Compliance:</strong> We automate GHG inventories and deliver audit-ready reporting aligned with ISSB/IFRS, GRI, and TCFD—reducing compliance friction while increasing confidence.</li>
                <li><strong className="text-foreground">Credibility Engine:</strong> Our proprietary credibility engines assess data integrity, detect fraud risk, and generate finance-grade credibility signals that investors and partners can rely on.</li>
              </ul>
            </div>

            <Separator className="my-8" />

            {/* Personal Data */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                  <UserCheck className="h-5 w-5 text-accent" />
                </div>
                <h2 className="text-2xl font-bold text-foreground m-0">Personal Data</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-4">As used in this Notice:</p>
              <ul className="space-y-2 text-muted-foreground mb-4">
                <li><strong className="text-foreground">"Customer"</strong> means an individual who (a) has contacted us through any means to find out more about any goods or services we provide, or (b) may, or has, entered into a contract with us for the supply of any goods or services by us.</li>
                <li><strong className="text-foreground">"Personal data"</strong> means data, whether true or not, about a customer who can be identified: (a) from that data; or (b) from that data and other information to which we have or are likely to have access.</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mb-2">
                Depending on the nature of your interaction with us, some examples of personal data which we may collect include:
              </p>
              <ul className="space-y-1 text-muted-foreground">
                <li>• Name, residential/company address, email address, telephone number</li>
              </ul>
            </div>

            <Separator className="my-8" />

            {/* Collection, Use and Disclosure */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-teal/10 flex items-center justify-center">
                  <Eye className="h-5 w-5 text-teal" />
                </div>
                <h2 className="text-2xl font-bold text-foreground m-0">Collection, Use and Disclosure of Personal Data</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We generally do not collect your personal data unless (a) it is provided to us voluntarily by you directly or via a third party who has been duly authorised by you to disclose your personal data to us (your "authorised representative") after (i) you (or your authorised representative) have been notified of the purposes for which the data is collected, and (ii) you (or your authorised representative) have provided written consent to the collection and usage of your personal data for those purposes, or (b) collection and use of personal data without consent is permitted or required by the PDPA or other laws.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We may collect, use and disclose your personal data for any or all of the following purposes:
              </p>
              <ol className="space-y-2 text-muted-foreground list-[lower-alpha] pl-6">
                <li>Providing, operating, administering, and improving our sustainability data engineering, reporting, analytics, credibility assessment, and advisory services;</li>
                <li>Designing, implementing, and supporting data pipelines, reporting systems, dashboards, digital MRV (dMRV) solutions, and sustainability-related digital infrastructure;</li>
                <li>Conducting sustainability reporting, assurance-readiness activities, verification support, analytics, benchmarking, and credibility or integrity assessments;</li>
                <li>Managing customer, partner, supplier, and stakeholder relationships, including onboarding, communications, and contractual administration;</li>
                <li>Responding to inquiries, proposals, tenders, requests for information, complaints, and feedback;</li>
                <li>Managing billing, invoicing, payments, credit assessments, and financial administration;</li>
                <li>Conducting internal analytics, risk management, fraud detection, data integrity checks, and quality assurance activities;</li>
                <li>Complying with applicable laws, regulations, standards, codes of practice, guidelines, and lawful requests from governmental or regulatory authorities;</li>
                <li>Carrying out internal business operations, including IT support, system administration, security monitoring, audits, training, and business continuity;</li>
                <li>Enabling collaboration with approved third-party service providers, technology partners, professional advisers, and subcontractors; and</li>
                <li>Any other purposes for which you have provided the information or where permitted or required by applicable law.</li>
              </ol>
            </div>

            <Separator className="my-8" />

            {/* Legitimate Interests */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                  <Shield className="h-5 w-5 text-accent" />
                </div>
                <h2 className="text-2xl font-bold text-foreground m-0">Reliance on the Legitimate Interests Exception</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-4">
                In compliance with the PDPA, we may collect, use or disclose your personal data without your consent for the legitimate interests of SusTek or approved intermediary. In relying on the legitimate interests exception of the PDPA, SusTek will assess the likely adverse effects on the individual and determine that the legitimate interests outweigh any adverse effect.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-2">
                In line with the legitimate interests' exception, we will collect, use or disclose your personal data for the following purposes:
              </p>
              <ul className="space-y-1 text-muted-foreground">
                <li>• Fraud detection and prevention;</li>
                <li>• Detection and prevention of misuse of services;</li>
                <li>• Network analysis to prevent fraud and financial crime, and perform credit analysis; and/or</li>
                <li>• Collection and use of personal data on company-issued devices to prevent data loss.</li>
              </ul>
            </div>

            <Separator className="my-8" />

            {/* Withdrawing Consent */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-teal/10 flex items-center justify-center">
                  <UserCheck className="h-5 w-5 text-teal" />
                </div>
                <h2 className="text-2xl font-bold text-foreground m-0">Withdrawing Your Consent</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-4">
                The consent that you provide for the collection, use and disclosure of your personal data will remain valid until such time it is being withdrawn by you in writing. You may withdraw consent and request us to stop collecting, using and/or disclosing your personal data for any or all of the purposes listed above by submitting your request in writing or via email to our Data Protection Officer at the contact details provided below.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Upon receipt of your written request to withdraw your consent, we may require reasonable time (depending on the complexity of the request and its impact on our relationship with you) for your request to be processed and for us to notify you of the consequences of us acceding to the same. In general, we shall seek to process your request within ten (10) business days of receiving it.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Please note that withdrawing consent does not affect our right to continue to collect, use and disclose personal data where such collection, use and disclosure without consent is permitted or required under applicable laws.
              </p>
            </div>

            <Separator className="my-8" />

            {/* Access and Correction */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-accent" />
                </div>
                <h2 className="text-2xl font-bold text-foreground m-0">Access to and Correction of Personal Data</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-4">
                If you wish to make (a) an access request for access to a copy of the personal data which we hold about you or information about the ways in which we use or disclose your personal data, or (b) a correction request to correct or update any of your personal data which we hold about you, you may submit your request in writing or via email to our Data Protection Officer at the contact details provided below.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Please note that a reasonable fee may be charged for an access request. If so, we will inform you of the fee before processing your request.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                We will respond to your request as soon as reasonably possible. In general, our response will be within thirty (30) business days.
              </p>
            </div>

            <Separator className="my-8" />

            {/* Protection of Personal Data */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-teal/10 flex items-center justify-center">
                  <Lock className="h-5 w-5 text-teal" />
                </div>
                <h2 className="text-2xl font-bold text-foreground m-0">Protection of Personal Data</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-4">
                To safeguard your personal data from unauthorised access, collection, use, disclosure, copying, modification, disposal or similar risks, we have introduced appropriate administrative, physical and technical measures such as:
              </p>
              <ul className="space-y-1 text-muted-foreground">
                <li>• Encryption of all files with personal data in storage and in transit</li>
                <li>• Access Rights to facility, and also company's system</li>
                <li>• Data anonymisation</li>
                <li>• Up-to-date antivirus or internet security protection software</li>
                <li>• Regular patching of operating system and other software</li>
                <li>• Web security measures against risks, and security review and testing performed regularly</li>
                <li>• VPN for in-house server</li>
                <li>• Using secured storage (e.g., Google Workspace - Google Drive, Microsoft Teams, SharePoint and OneDrive)</li>
                <li>• 2FA for organization's IT account access</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                You should be aware, however, that no method of transmission over the Internet or method of electronic storage is completely secure. While security cannot be guaranteed, we strive to protect the security of your information and are constantly reviewing and enhancing our information security measures.
              </p>
            </div>

            <Separator className="my-8" />

            {/* Retention */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-accent" />
                </div>
                <h2 className="text-2xl font-bold text-foreground m-0">Retention of Personal Data</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                We may retain your personal data for as long as it is necessary to fulfil the purpose for which it was collected, or as required or permitted by applicable laws. We will cease to retain your personal data, or remove the means by which the data can be associated with you, as soon as it is reasonable to assume that such retention no longer serves the purpose for which the personal data was collected, and is no longer necessary for legal or business purposes.
              </p>
            </div>

            <Separator className="my-8" />

            {/* International Transfers */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-teal/10 flex items-center justify-center">
                  <Globe className="h-5 w-5 text-teal" />
                </div>
                <h2 className="text-2xl font-bold text-foreground m-0">Transfers of Personal Data Outside of Singapore</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We may, from time to time, transfer, store, process, or allow access to your personal data outside of Singapore in connection with our use of cloud-based systems, digital collaboration platforms, and the engagement of overseas or offshore personnel and service providers.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Where personal data is transferred or accessed outside Singapore, we will take reasonable and appropriate steps to ensure that the personal data continues to receive a standard of protection that is at least comparable to that provided under the PDPA. These steps may include:
              </p>
              <ol className="space-y-1 text-muted-foreground list-[lower-alpha] pl-6">
                <li>Contractual obligations requiring overseas recipients to protect personal data in accordance with PDPA-comparable standards;</li>
                <li>Access controls, role-based permissions, and authentication measures to restrict data access;</li>
                <li>Encryption of personal data in transit and at rest;</li>
                <li>Security assessments and due diligence of cloud service providers and overseas vendors; and</li>
                <li>Internal policies governing cross-border data access, remote work, and offshore operations.</li>
              </ol>
            </div>

            <Separator className="my-8" />

            {/* DPO Contact */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                  <Mail className="h-5 w-5 text-accent" />
                </div>
                <h2 className="text-2xl font-bold text-foreground m-0">Data Protection Officer</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-6">
                You may contact our Data Protection Officer if you have any enquiries or feedback on our personal data protection policies and procedures, or if you wish to make any request:
              </p>
              <Card className="border-teal/20 bg-teal/5">
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <UserCheck className="h-5 w-5 text-teal" />
                      <span className="text-foreground"><strong>Name of DPO:</strong> Danny</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="h-5 w-5 text-teal" />
                      <span className="text-foreground"><strong>Contact No.:</strong> +65 6679 6000</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail className="h-5 w-5 text-teal" />
                      <span className="text-foreground"><strong>Email Address:</strong> <a href="mailto:dpo@sustek.ai" className="text-teal hover:underline">dpo@sustek.ai</a></span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Separator className="my-8" />

            {/* Changes to Notice */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-teal/10 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-teal" />
                </div>
                <h2 className="text-2xl font-bold text-foreground m-0">Effect of Notice and Changes to Notice</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-4">
                This Notice applies in conjunction with any other notices, contractual clauses and consent clauses that apply in relation to the collection, use and disclosure of your personal data by us.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                We may revise this Notice from time to time without any prior notice. You may determine if any such revision has taken place by referring to the date on which this Notice was last updated. Your continued use of our services constitutes your acknowledgement and acceptance of such changes.
              </p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
